import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { AccountAnimalTodayReady, QuizAttemptResult } from '../../../shared/types/api';
import { api } from '../../lib/api';
import { SIMULATED_TRAIL_DISCLOSURE } from '../../lib/simulatedTrailDisclosure';
import { AnimalDailyActivities } from './AnimalDailyActivities';
import { isLaunchActivitySpecies } from './animalActivityPacks';

interface ActivityConfig {
  prompt?: string;
  choices?: string[];
  correctIndex?: number;
}

function activityConfig(value: Record<string, unknown>): ActivityConfig {
  return {
    prompt: typeof value.prompt === 'string' ? value.prompt : undefined,
    choices:
      Array.isArray(value.choices) && value.choices.every((choice) => typeof choice === 'string')
        ? value.choices
        : undefined,
    correctIndex: typeof value.correctIndex === 'number' ? value.correctIndex : undefined,
  };
}

function PlayableActivity({
  activity,
}: {
  activity: AccountAnimalTodayReady['activities'][number];
}) {
  const config = activityConfig(activity.config);
  const [choice, setChoice] = useState<number | null>(null);
  const playable =
    ['habitat_match', 'adaptation_match'].includes(activity.activityType) &&
    config.choices &&
    config.correctIndex !== undefined;
  return (
    <article className="learning-activity-card">
      <div className="learning-activity-copy">
        <h4>{activity.title}</h4>
        <p>{activity.instructions}</p>
      </div>
      {playable && config.choices ? (
        <fieldset>
          <legend>{config.prompt ?? activity.title}</legend>
          {config.choices.map((label, index) => (
            <label key={label}>
              <input
                type="radio"
                name={`activity-${activity.id}`}
                checked={choice === index}
                onChange={() => setChoice(index)}
              />
              {label}
            </label>
          ))}
          <p className="learning-feedback" role="status" aria-live="polite">
            {choice === null
              ? 'Choose an answer when you are ready.'
              : choice === config.correctIndex
                ? 'That’s it! You matched the clue.'
                : 'Good try. Look at the habitat clue and choose again.'}
          </p>
        </fieldset>
      ) : (
        <p className="learning-quiet-note">This is a talk-together activity. Nothing is saved.</p>
      )}
    </article>
  );
}

export function AccountLearningPanel({
  today,
  entitlementId,
  onClose,
}: {
  today: AccountAnimalTodayReady;
  entitlementId: string;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);

  function keepDialogFocus(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  return (
    <section
      ref={panelRef}
      className="account-learning-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="learning-panel-title"
      onKeyDown={keepDialogFocus}
    >
      <div className="learning-panel-heading">
        <div>
          <p className="eyebrow">Today’s learning · {today.date}</p>
          <h2 id="learning-panel-title">Explore the trail</h2>
        </div>
        <button type="button" className="secondary-action" onClick={onClose} autoFocus>
          Close
        </button>
      </div>
      <AccountLearningJourney today={today} entitlementId={entitlementId} />
    </section>
  );
}

export function AccountLearningJourney({
  today,
  entitlementId,
  animalName,
  animalSpeciesName,
}: {
  today: AccountAnimalTodayReady;
  entitlementId: string;
  animalName?: string;
  animalSpeciesName?: string;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const totalQuestions = today.quiz.questions.length;
  const complete = useMemo(
    () =>
      totalQuestions > 0 && today.quiz.questions.every((question) => Boolean(answers[question.id])),
    [answers, today.quiz.questions, totalQuestions],
  );
  const canSubmit = complete;
  const answeredCount = useMemo(
    () => today.quiz.questions.filter((question) => Boolean(answers[question.id])).length,
    [answers, today.quiz.questions],
  );
  const launchActivitySpecies = isLaunchActivitySpecies(animalSpeciesName)
    ? animalSpeciesName
    : null;
  const launchAnimalName = animalName ?? 'Your animal';
  const otherActivities = today.activities;
  const activityGridClassName =
    otherActivities.length === 1 ? 'learning-activity-grid is-single' : 'learning-activity-grid';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!complete) {
      setMessage(`Answer all ${totalQuestions} questions first.`);
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      setResult(
        await api.submitAccountAnimalQuiz(
          entitlementId,
          today.date,
          today.quiz.questions.map((question) => ({
            questionId: question.id,
            choiceId: answers[question.id]!,
          })),
        ),
      );
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'The quiz could not be checked yet.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="learning-journey">
      <div className="learning-note-grid">
        <article className="learning-fact-card">
          <p className="species-label">Factual wildlife note</p>
          <h3>Did you know?</h3>
          <p>{today.fact.text}</p>
        </article>
        <article className="learning-story-card">
          <p className="species-label">Simulated trail story</p>
          <h3>{today.story.title}</h3>
          <p>{today.story.narrative}</p>
          <p className="learning-quiet-note">
            Science connection: {today.story.educationalExplanation}
          </p>
        </article>
      </div>

      <form className="learning-quiz" onSubmit={(event) => void submit(event)}>
        <h3>Trail quiz</h3>
        <p className="learning-quiet-note">
          {totalQuestions === 0
            ? 'No quiz questions are ready yet. Check back soon.'
            : complete
              ? 'You answered every question. Tap "Score answers" to check your trail quiz.'
              : `${answeredCount} of ${totalQuestions} questions answered`}
        </p>
        {today.quiz.questions.map((question, questionIndex) => {
          const feedback = result?.results.find((item) => item.questionId === question.id);
          return (
            <fieldset key={question.id}>
              <legend>
                {questionIndex + 1}. {question.prompt}
              </legend>
              {question.choices.map((choice) => (
                <label key={choice.id}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={choice.id}
                    checked={answers[question.id] === choice.id}
                    disabled={Boolean(result)}
                    onChange={() =>
                      setAnswers((current) => ({ ...current, [question.id]: choice.id }))
                    }
                  />
                  {choice.label}
                </label>
              ))}
              {feedback && (
                <p className="learning-feedback" role="status">
                  {feedback.correct ? 'Correct! ' : 'Good try. '}
                  {feedback.explanation}
                </p>
              )}
            </fieldset>
          );
        })}
        {message && <p role="alert">{message}</p>}
        {result ? (
          <p className="learning-score" role="status" aria-live="polite">
            You found {result.correctCount} of {result.totalCount} answers. You can review the clues
            again anytime.
          </p>
        ) : (
          <button type="submit" className="primary-quiz-submit" disabled={!canSubmit || busy}>
            {busy ? 'Scoring…' : 'Score answers'}
          </button>
        )}
      </form>

      <section className="learning-activities-section" aria-labelledby="learning-activities-title">
        <div className="learning-activities-heading">
          <p className="eyebrow">Play and explore</p>
          <h3 id="learning-activities-title">Today’s extra activities</h3>
        </div>
        <div className="learning-activity-groups">
          {launchActivitySpecies && (
            <div className="learning-activity-group">
              <p className="learning-activity-group-label">{launchAnimalName}’s activity picks</p>
              <AnimalDailyActivities
                key={`${launchActivitySpecies}|${today.date}`}
                animalName={launchAnimalName}
                speciesName={launchActivitySpecies}
                date={today.date}
              />
            </div>
          )}
          {otherActivities.length > 0 && (
            <div className="learning-activity-group">
              <p className="learning-activity-group-label">
                {launchActivitySpecies ? 'More trail challenges' : 'Trail challenges'}
              </p>
              <div className={activityGridClassName}>
                {otherActivities.map((activity) => (
                  <PlayableActivity key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <p className="simulated-data-note">
        {SIMULATED_TRAIL_DISCLOSURE} This story and map do not show a real animal or live tracking
        device.
      </p>
    </div>
  );
}
